import { useState } from 'react'
import { unitId, actionId } from '@/annotations'
import { WizardSplit, WizardCard, WizardHeading, WizardSubheading, WizardProse, WizardButtons, ProcedureStep } from '@/components/WizardParts'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Icon } from '@/components/Icon'
import { OB_WZ_1_COPY } from '@/copy/onboarding'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'

/**
 * `ob-wz-1` — stage 2's first pane (design/12-onboarding-and-credentials.md §12.4.1/§12.4.2).
 * Collects the token only; `Next` is a client-side transition (`src/layers/onboarding/state.ts`'s
 * own header comment explains why there is no per-field validation call here anymore).
 *
 * **`data-cmp="pane"`/`"panel"` and the "Next" button's own icon added W156** — the spec's own
 * Components table named all three and none had been built. **Not built**: the spec's own "icon in
 * `procedure-step-number`" sub-case — `ProcedureStepNumber` (`WizardParts.tsx`) is shared by every
 * numbered-step screen in the app, and changing its own rendering unilaterally here would be a
 * wide-blast-radius change to already-shipped screens this pass did not re-verify; reported rather
 * than risked.
 *
 * **`data-act` corrected from `nav.ob-wz-2` to `workspace.connect` (found by `tools/uispec/
 * l1_conform.py`, flagged by a peer session's cross-service review).** Rule B1
 * (`_family-onboarding.md`): "`workspaceConnect` is composed across panes 1–3 and sent once" —
 * the spec's own Actions table names THIS pane's own forward control as the operation the whole
 * three-pane flow is building toward, not a plain wizard-internal navigation. The click itself is
 * still only a client-side transition (`onNext`) — no network call fires here, per B1 — only the
 * `data-act` tag changed, to match what the spec (and the gate) actually expect it to name.
 *
 * **`initialCredential` added** — `state.ts`'s own `BACK` case now carries the credential backward
 * from stage 2, so a return trip here seeds the field instead of remounting it blank.
 */
export function ObWz1({ initialCredential, onNext }: { initialCredential?: string; onNext: (credential: string) => void }) {
  const [token, setToken] = useState(initialCredential ?? '')

  return (
    <div data-scr={unitId('ob-wz-1')} data-cmp="pane" className="p-10">
      <WizardSplit>
        <div>
          <WizardHeading>{OB_WZ_1_COPY.heading}</WizardHeading>
          <WizardProse>{OB_WZ_1_COPY.proseOne}</WizardProse>
          <WizardProse>{OB_WZ_1_COPY.proseTwo}</WizardProse>
          <WizardSubheading>{OB_WZ_1_COPY.subheading}</WizardSubheading>
          <div data-cmp="panel" className="bg-panel border border-bd rounded-2 overflow-hidden">
            <ProcedureStep ordinal="01" boxed>
              <b>{OB_WZ_1_COPY.step1Title}</b>
              <Note>{OB_WZ_1_COPY.step1Note}</Note>
            </ProcedureStep>
            <ProcedureStep ordinal="02" boxed>
              <b>{OB_WZ_1_COPY.step2Title}</b>
              <Note>{OB_WZ_1_COPY.step2Note}</Note>
            </ProcedureStep>
            <ProcedureStep ordinal="03" boxed>
              <b>{OB_WZ_1_COPY.step3Title}</b>
              <Note>{OB_WZ_1_COPY.step3Note}</Note>
            </ProcedureStep>
          </div>
        </div>
        <WizardCard>
          <h4 className="m-0 mb-7 text-body-lg font-semibold">{OB_WZ_1_COPY.cardHeading}</h4>
          <Field label={OB_WZ_1_COPY.tokenLabel}>
            <input
              type="password"
              className={TEXT_INPUT_CLASSNAME}
              style={TEXT_INPUT_STYLE}
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </Field>
          <Note>{OB_WZ_1_COPY.tokenNote}</Note>
          <WizardButtons>
            <span className="text-small text-tx2 mr-auto">{OB_WZ_1_COPY.footNote}</span>
            <Button data-act={actionId('workspace.connect')} disabled={!token} onClick={() => onNext(token)}>
              {OB_WZ_1_COPY.nextButton}
              <Icon name="i-chevR" size="s" />
            </Button>
          </WizardButtons>
        </WizardCard>
      </WizardSplit>
    </div>
  )
}
